class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.suggestionsTemplate = this.querySelector('template[data-search-suggestions]');
    this.loadingMarkup = this.predictiveSearchResults.innerHTML;
    this.statusElement = this.querySelector('.predictive-search-status');
    this.resultsId = `${this.input.id}-predictive-results`;
    this.isOpen = false;
    this.requestId = 0;
    this.searchTerm = '';
    this.renderedSearchTerm = null;
    this.onViewportResize = () => {
      if (this.isOpen) this.setResultsMaxHeight();
    };

    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);
    this.setupEventListeners();
  }

  connectedCallback() {
    window.addEventListener('resize', this.onViewportResize);
    window.visualViewport?.addEventListener('resize', this.onViewportResize);
  }

  disconnectedCallback() {
    this.cancelPendingRequest();
    clearTimeout(this.liveRegionTimeout);
    window.removeEventListener('resize', this.onViewportResize);
    window.visualViewport?.removeEventListener('resize', this.onViewportResize);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
    this.resetButton?.addEventListener('click', this.clearSelection.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onInput() {
    // Invalidate old requests immediately, before SearchForm's input debounce.
    // A response for the previous text must never replace the latest query.
    this.close();
    this.removeAttribute('results');
    this.removeAttribute('showing-suggestions');
    this.renderedSearchTerm = null;
    this.predictiveSearchResults.innerHTML = this.loadingMarkup;
    super.onChange();
    if (!this.getQuery()) this.showDefaultSuggestions();
  }

  onChange() {
    super.onChange();
    this.searchTerm = this.getQuery();

    if (!this.searchTerm) {
      this.showDefaultSuggestions();
      return;
    }

    // A debounced event can run after the visitor has closed the search modal.
    if (!this.contains(document.activeElement)) return;
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery()) event.preventDefault();
  }

  onFormReset(event) {
    this.clearSelection();
    super.onFormReset(event);
    this.searchTerm = '';
    this.showDefaultSuggestions();
  }

  onFocus() {
    super.onChange();
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm) {
      this.showDefaultSuggestions();
    } else if (this.getAttribute('results') === 'true' && this.renderedSearchTerm === currentSearchTerm) {
      this.open();
    } else {
      this.onChange();
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeydown(event) {
    if (event.target !== this.input || event.isComposing) return;

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.isOpen) this.onFocus();
      this.switchOption(event.key === 'ArrowUp' ? 'up' : 'down');
    } else if (event.key === 'Enter' && this.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  clearSelection() {
    this.querySelectorAll('[aria-selected="true"]').forEach((element) => {
      element.setAttribute('aria-selected', 'false');
    });
    this.input.removeAttribute('aria-activedescendant');
  }

  switchOption(direction) {
    if (!this.isOpen) return;

    const options = Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]'))
      .filter((element) => element.getClientRects().length > 0);
    if (!options.length) return;

    const selectedIndex = options.findIndex((element) => element.getAttribute('aria-selected') === 'true');
    const nextIndex = selectedIndex < 0
      ? (direction === 'up' ? options.length - 1 : 0)
      : (selectedIndex + (direction === 'up' ? -1 : 1) + options.length) % options.length;
    const selectedOption = options[nextIndex];

    this.clearSelection();
    if (this.statusElement) this.statusElement.textContent = '';
    selectedOption.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', selectedOption.id);
    selectedOption.scrollIntoView({ block: 'nearest' });
  }

  selectOption() {
    const selected = this.predictiveSearchResults.querySelector('[aria-selected="true"]');
    const target = selected?.matches('a, button') ? selected : selected?.querySelector('a, button');
    target?.click();
  }

  cancelPendingRequest() {
    this.requestId += 1;
    this.abortController?.abort();
    this.abortController = null;
  }

  showDefaultSuggestions() {
    this.cancelPendingRequest();
    this.searchTerm = '';
    this.removeAttribute('loading');

    if (this.getQuery() || !this.suggestionsTemplate || !this.contains(document.activeElement)) {
      this.close();
      return;
    }

    if (this.hasAttribute('showing-suggestions') && this.getAttribute('results') === 'true') {
      this.open();
      return;
    }

    this.renderSearchResults(this.suggestionsTemplate.innerHTML, '', true);
  }

  async getSearchResults(searchTerm) {
    this.cancelPendingRequest();
    const requestId = this.requestId;
    // Preserve spaces and punctuation in cache keys ("a-b" and "a b" differ).
    const queryKey = searchTerm;

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    this.abortController = new AbortController();
    this.clearSelection();
    this.removeAttribute('results');
    this.removeAttribute('showing-suggestions');
    this.predictiveSearchResults.innerHTML = this.loadingMarkup;
    this.setLiveRegionLoadingState();
    this.open();

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
        { signal: this.abortController.signal }
      );
      if (!response.ok) throw new Error(`Predictive search: ${response.status}`);

      const text = await response.text();
      if (requestId !== this.requestId || this.getQuery() !== searchTerm) return;

      const section = new DOMParser().parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search');
      if (!section?.querySelector('[role="listbox"]')) throw new Error('Missing predictive search results');

      const resultsMarkup = section.innerHTML;
      document.querySelectorAll('predictive-search').forEach((instance) => {
        if (!instance.cachedResults) return;
        const keys = Object.keys(instance.cachedResults);
        if (keys.length >= 40 && !instance.cachedResults[queryKey]) delete instance.cachedResults[keys[0]];
        instance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup, searchTerm);
    } catch (error) {
      if (requestId !== this.requestId || error.name === 'AbortError') return;
      // Keep the standard search form usable if Shopify is temporarily unavailable.
      this.close();
    }
  }

  setLiveRegionLoadingState() {
    this.setLiveRegionText(this.getAttribute('data-loading-text'));
    this.setAttribute('loading', 'true');
    this.predictiveSearchResults.setAttribute('aria-busy', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    clearTimeout(this.liveRegionTimeout);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    this.liveRegionTimeout = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm, isSuggestion = false) {
    if (this.getQuery() !== searchTerm || !this.contains(document.activeElement)) return;
    this.clearSelection();
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // Each header layout and the search page can coexist. Scope the server's
    // shared IDs so their listboxes and keyboard targets stay independent.
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
      const previousId = element.id;
      element.id = previousId === 'predictive-search-results'
        ? this.resultsId : `${this.resultsId}-${previousId}`;
      ids.set(previousId, element.id);
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby], [aria-describedby]').forEach((element) => {
      ['aria-labelledby', 'aria-describedby'].forEach((attribute) => {
        if (!element.hasAttribute(attribute)) return;
        element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/)
          .map((id) => ids.get(id) || id).join(' '));
      });
    });
    this.predictiveSearchResults.querySelectorAll('[role="option"]').forEach((element, index) => {
      if (!element.id) element.id = `${this.resultsId}-option-${index + 1}`;
    });

    this.renderedSearchTerm = searchTerm;
    this.setAttribute('results', 'true');
    this.toggleAttribute('showing-suggestions', isSuggestion);
    this.removeAttribute('loading');
    this.predictiveSearchResults.removeAttribute('aria-busy');
    const count = this.predictiveSearchResults.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(count?.textContent.trim());
    this.open();
  }

  setResultsMaxHeight() {
    const viewport = window.visualViewport;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const availableHeight = viewportBottom - this.predictiveSearchResults.getBoundingClientRect().top - 12;
    this.predictiveSearchResults.style.maxHeight = `${Math.max(0, availableHeight)}px`;
  }

  open() {
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.setResultsMaxHeight();
  }

  close(clearSearchTerm = false) {
    this.cancelPendingRequest();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.renderedSearchTerm = null;
      this.removeAttribute('results');
      this.removeAttribute('showing-suggestions');
      super.onChange();
    }
    this.clearSelection();
    clearTimeout(this.liveRegionTimeout);
    this.statusElement?.setAttribute('aria-hidden', 'true');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('aria-busy');
    this.predictiveSearchResults.style.removeProperty('max-height');
  }
}

customElements.define('predictive-search', PredictiveSearch);
